import React, { useMemo, useState, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Search, Save, ImageOff, MoreVertical, Edit, ChevronRight, Home, Grid3x3, List, ChevronDown, Globe, RefreshCw, Undo2, Check, Star, Layers, Eye, Plus, X, Pencil, Trash2, FolderPlus, ArrowRightLeft, Tag } from 'lucide-react';
import { type Scope, type GlobalOption, type OptionOverride, type ResolvedOption, type OptionLevel, type NavigationLevel, type BreadcrumbItem, type Catalog, levelConfig, initialGlobalOptions, initialOverrides, initialLevelAssignments, initialCatalogs, resolveOption } from '../data/optionsData';
import { ScopeSelector } from '../components/options/ScopeSelector';
import { EditOptionModal } from '../components/options/EditOptionModal';
import { CreateOptionModal } from '../components/options/CreateOptionModal';
export function OptionsAltPage() {
  const navigate = useNavigate();
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const [scope, setScope] = useState<Scope>({
    level: 'global',
    name: 'All Options'
  });
  const [globalOptions, setGlobalOptions] = useState<GlobalOption[]>(initialGlobalOptions);
  const [overrides, setOverrides] = useState<OptionOverride[]>(initialOverrides);
  const [levelAssignments, setLevelAssignments] = useState<Record<string, OptionLevel>>(initialLevelAssignments);
  const [searchQuery, setSearchQuery] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOptionForEdit, setSelectedOptionForEdit] = useState<ResolvedOption | null>(null);
  const [levelFilter, setLevelFilter] = useState<OptionLevel | 'all'>('all');
  const [levelDropdownOpen, setLevelDropdownOpen] = useState(false);
  const [levelPickerOpen, setLevelPickerOpen] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  // Catalog management state
  const [catalogs, setCatalogs] = useState<Catalog[]>(initialCatalogs);
  const [catalogMenuOpen, setCatalogMenuOpen] = useState<string | null>(null);
  const [creatingCatalog, setCreatingCatalog] = useState(false);
  const [newCatalogName, setNewCatalogName] = useState('');
  const [newCatalogDesc, setNewCatalogDesc] = useState('');
  const [renamingCatalogId, setRenamingCatalogId] = useState<string | null>(null);
  const [renameCatalogName, setRenameCatalogName] = useState('');
  const [deletingCatalogId, setDeletingCatalogId] = useState<string | null>(null);
  // Subcategory management state
  const [subcatMenuOpen, setSubcatMenuOpen] = useState<string | null>(null);
  const [renamingSubcat, setRenamingSubcat] = useState<{
    catalogId: string;
    oldName: string;
  } | null>(null);
  const [renameSubcatName, setRenameSubcatName] = useState('');
  const [creatingSubcatForCatalog, setCreatingSubcatForCatalog] = useState<string | null>(null);
  const [newSubcatName, setNewSubcatName] = useState('');
  // Move option state
  const [moveModalOption, setMoveModalOption] = useState<ResolvedOption | null>(null);
  const [moveToCatalog, setMoveToCatalog] = useState('');
  const [moveToSubcategory, setMoveToSubcategory] = useState('');
  const [moveToVariantSet, setMoveToVariantSet] = useState('');
  // Default option per variant set (key: "category|subcategory|variantSet" → optionId)
  const [defaultOptionIds, setDefaultOptionIds] = useState<Record<string, string>>({});
  const getVariantSetKey = (option: ResolvedOption) => `${option.category}|${option.subcategory}|${option.variantSet}`;
  const handleSetDefault = (option: ResolvedOption) => {
    const key = getVariantSetKey(option);
    setDefaultOptionIds(prev => {
      // Toggle off if already default
      if (prev[key] === option.id) {
        const next = {
          ...prev
        };
        delete next[key];
        return next;
      }
      return {
        ...prev,
        [key]: option.id
      };
    });
    setOpenDropdown(null);
  };
  const isDefaultOption = (option: ResolvedOption) => defaultOptionIds[getVariantSetKey(option)] === option.id;
  const resolvedOptions = useMemo(() => globalOptions.map(opt => resolveOption(opt, scope, overrides)), [scope, overrides, globalOptions]);
  const currentLevel: NavigationLevel = breadcrumbs.length === 0 ? 'category' : breadcrumbs.length === 1 ? 'subcategory' : breadcrumbs.length === 2 ? 'variantSet' : 'options';
  const getSubcategories = (category: string) => Array.from(new Set(resolvedOptions.filter(o => o.category === category).map(o => o.subcategory)));
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);else next.add(category);
      return next;
    });
  };
  const currentItems = useMemo(() => {
    let items: any[];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = resolvedOptions.filter(opt => opt.name.toLowerCase().includes(query) || opt.effectiveDescription?.toLowerCase().includes(query) || opt.tags?.some(tag => tag.toLowerCase().includes(query)));
    } else if (breadcrumbs.length === 0) {
      const categories = Array.from(new Set(resolvedOptions.map(o => o.category)));
      return categories.map(cat => {
        const catItems = resolvedOptions.filter(o => o.category === cat);
        const filteredByLevel = levelFilter === 'all' ? catItems : levelFilter === null ? catItems.filter(o => !levelAssignments[o.id]) : catItems.filter(o => levelAssignments[o.id] === levelFilter);
        return {
          id: cat,
          name: cat,
          count: filteredByLevel.length,
          enabledCount: filteredByLevel.filter(o => o.effectiveEnabled).length,
          type: 'category' as const
        };
      });
    } else if (breadcrumbs.length === 1) {
      const category = breadcrumbs[0].value;
      const subcategories = Array.from(new Set(resolvedOptions.filter(o => o.category === category).map(o => o.subcategory)));
      return subcategories.map(subcat => {
        const subcatItems = resolvedOptions.filter(o => o.category === category && o.subcategory === subcat);
        const filteredByLevel = levelFilter === 'all' ? subcatItems : levelFilter === null ? subcatItems.filter(o => !levelAssignments[o.id]) : subcatItems.filter(o => levelAssignments[o.id] === levelFilter);
        return {
          id: subcat,
          name: subcat,
          count: filteredByLevel.length,
          enabledCount: filteredByLevel.filter(o => o.effectiveEnabled).length,
          type: 'subcategory' as const
        };
      });
    } else if (breadcrumbs.length === 2) {
      const category = breadcrumbs[0].value;
      const subcategory = breadcrumbs[1].value;
      const variantSets = Array.from(new Set(resolvedOptions.filter(o => o.category === category && o.subcategory === subcategory).map(o => o.variantSet)));
      return variantSets.map(vs => {
        const vsItems = resolvedOptions.filter(o => o.category === category && o.subcategory === subcategory && o.variantSet === vs);
        const filteredByLevel = levelFilter === 'all' ? vsItems : levelFilter === null ? vsItems.filter(o => !levelAssignments[o.id]) : vsItems.filter(o => levelAssignments[o.id] === levelFilter);
        return {
          id: vs,
          name: vs,
          count: filteredByLevel.length,
          enabledCount: filteredByLevel.filter(o => o.effectiveEnabled).length,
          type: 'variantSet' as const
        };
      });
    } else {
      const category = breadcrumbs[0].value,
        subcategory = breadcrumbs[1].value,
        variantSet = breadcrumbs[2].value;
      items = resolvedOptions.filter(o => o.category === category && o.subcategory === subcategory && o.variantSet === variantSet);
    }
    if (levelFilter !== 'all' && items) {
      items = levelFilter === null ? items.filter((o: any) => !levelAssignments[o.id]) : items.filter((o: any) => levelAssignments[o.id] === levelFilter);
    }
    return items;
  }, [breadcrumbs, resolvedOptions, searchQuery, levelFilter, levelAssignments]);
  const isShowingOptions = currentLevel === 'options' || searchQuery.trim().length > 0;
  const handleNavigate = (item: any) => {
    if (item.type === 'category') setBreadcrumbs([{
      label: item.name,
      level: 'category',
      value: item.name
    }]);else if (item.type === 'subcategory') setBreadcrumbs([...breadcrumbs, {
      label: item.name,
      level: 'subcategory',
      value: item.name
    }]);else if (item.type === 'variantSet') setBreadcrumbs([...breadcrumbs, {
      label: item.name,
      level: 'variantSet',
      value: item.name
    }]);
  };
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());
  const toggleSubcategory = (key: string) => {
    setExpandedSubcategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);else next.add(key);
      return next;
    });
  };
  const getVariantSets = (category: string, subcategory: string) => Array.from(new Set(resolvedOptions.filter(o => o.category === category && o.subcategory === subcategory).map(o => o.variantSet)));
  const handleSidebarNavigate = (category: string, subcategory?: string, variantSet?: string) => {
    if (variantSet && subcategory) setBreadcrumbs([{
      label: category,
      level: 'category',
      value: category
    }, {
      label: subcategory,
      level: 'subcategory',
      value: subcategory
    }, {
      label: variantSet,
      level: 'variantSet',
      value: variantSet
    }]);else if (subcategory) setBreadcrumbs([{
      label: category,
      level: 'category',
      value: category
    }, {
      label: subcategory,
      level: 'subcategory',
      value: subcategory
    }]);else setBreadcrumbs([{
      label: category,
      level: 'category',
      value: category
    }]);
  };
  const handleBreadcrumbClick = (index: number) => setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  const handleOpenEdit = (option: ResolvedOption) => {
    setSelectedOptionForEdit(option);
    setEditModalOpen(true);
    setOpenDropdown(null);
  };
  const handleSaveOverride = (override: OptionOverride) => {
    const filtered = overrides.filter(o => !(o.optionId === override.optionId && o.level === override.level && o.levelId === override.levelId));
    setOverrides([...filtered, override]);
  };
  const handleSaveGlobal = (optionId: string, updates: Partial<GlobalOption>) => {
    setGlobalOptions(prev => prev.map(opt => opt.id === optionId ? {
      ...opt,
      ...updates
    } : opt));
  };
  const handleResetOverride = (option: ResolvedOption) => {
    if (scope.level === 'global') return;
    const levelId = scope.level === 'region' ? scope.regionId : scope.communityId;
    setOverrides(overrides.filter(o => !(o.optionId === option.id && o.level === scope.level && o.levelId === levelId)));
    setOpenDropdown(null);
  };
  const handleCreateNewOption = (option: GlobalOption) => setGlobalOptions(prev => [...prev, option]);
  const handleLinkExistingOption = (option: GlobalOption) => {
    if (!globalOptions.find(o => o.id === option.id)) setGlobalOptions(prev => [...prev, option]);
  };
  const handleSetLevel = (optionId: string, level: OptionLevel) => {
    setLevelAssignments(prev => ({
      ...prev,
      [optionId]: level
    }));
    setLevelPickerOpen(null);
    setOpenDropdown(null);
  };
  const stats = useMemo(() => {
    const total = resolvedOptions.length,
      enabled = resolvedOptions.filter(o => o.effectiveEnabled).length;
    const standard = resolvedOptions.filter(o => o.effectiveIsStandard).length;
    const overridden = resolvedOptions.filter(o => o.hasOverride && o.overrideSource === scope.level).length;
    const inherited = resolvedOptions.filter(o => scope.level !== 'global' && o.overrideSource !== scope.level && o.overrideSource !== 'global').length;
    return {
      total,
      enabled,
      standard,
      overridden,
      inherited
    };
  }, [resolvedOptions, scope]);
  const levelStats = useMemo(() => {
    const counts: Record<string, number> = {
      standard: 0,
      'level-1': 0,
      'level-2': 0,
      'level-3': 0,
      unassigned: 0
    };
    resolvedOptions.forEach(o => {
      const level = levelAssignments[o.id];
      if (level) counts[level]++;else counts['unassigned']++;
    });
    return counts;
  }, [resolvedOptions, levelAssignments]);
  // --- Subcategory handlers ---
  const getSubcategoriesForCatalog = (catalogId: string) => {
    const catalog = catalogs.find(c => c.id === catalogId);
    if (!catalog) return [];
    const managed = catalog.subcategories || [];
    const derived = Array.from(new Set(resolvedOptions.filter(o => o.category === catalog.name).map(o => o.subcategory)));
    return Array.from(new Set([...managed, ...derived])).filter(Boolean);
  };
  const handleCreateSubcategory = (catalogId: string) => {
    if (!newSubcatName.trim()) return;
    setCatalogs(prev => prev.map(c => c.id === catalogId && !c.subcategories.includes(newSubcatName.trim()) ? {
      ...c,
      subcategories: [...c.subcategories, newSubcatName.trim()]
    } : c));
    setNewSubcatName('');
    setCreatingSubcatForCatalog(null);
  };
  const handleRenameSubcategory = (catalogId: string, oldName: string) => {
    if (!renameSubcatName.trim() || renameSubcatName.trim() === oldName) {
      setRenamingSubcat(null);
      return;
    }
    const catalog = catalogs.find(c => c.id === catalogId);
    if (!catalog) return;
    const newName = renameSubcatName.trim();
    // Update catalog's subcategories array
    setCatalogs(prev => prev.map(c => c.id === catalogId ? {
      ...c,
      subcategories: c.subcategories.map(s => s === oldName ? newName : s)
    } : c));
    // Update all options in this catalog with the old subcategory
    setGlobalOptions(prev => prev.map(opt => opt.category === catalog.name && opt.subcategory === oldName ? {
      ...opt,
      subcategory: newName
    } : opt));
    // Update breadcrumbs if viewing the renamed subcategory
    if (breadcrumbs[0]?.value === catalog.name && breadcrumbs[1]?.value === oldName) {
      setBreadcrumbs(prev => prev.map((b, i) => i === 1 ? {
        ...b,
        label: newName,
        value: newName
      } : b));
    }
    setRenamingSubcat(null);
    setRenameSubcatName('');
  };
  const handleDeleteSubcategory = (catalogId: string, subcatName: string) => {
    const catalog = catalogs.find(c => c.id === catalogId);
    if (!catalog) return;
    const remaining = (catalog.subcategories || []).filter(s => s !== subcatName);
    const fallback = remaining[0] || 'Uncategorized';
    // Update catalog
    setCatalogs(prev => prev.map(c => c.id === catalogId ? {
      ...c,
      subcategories: remaining
    } : c));
    // Move options to fallback subcategory
    setGlobalOptions(prev => prev.map(opt => opt.category === catalog.name && opt.subcategory === subcatName ? {
      ...opt,
      subcategory: fallback
    } : opt));
    // Reset breadcrumbs if viewing the deleted subcategory
    if (breadcrumbs[0]?.value === catalog.name && breadcrumbs[1]?.value === subcatName) {
      setBreadcrumbs([{
        label: catalog.name,
        level: 'category',
        value: catalog.name
      }]);
    }
    setSubcatMenuOpen(null);
  };
  // --- Move option handler ---
  const handleOpenMoveModal = (option: ResolvedOption) => {
    setMoveModalOption(option);
    setMoveToCatalog(option.category);
    setMoveToSubcategory(option.subcategory);
    setMoveToVariantSet(option.variantSet);
    setOpenDropdown(null);
  };
  const handleMoveOption = () => {
    if (!moveModalOption || !moveToCatalog.trim() || !moveToSubcategory.trim() || !moveToVariantSet.trim()) return;
    setGlobalOptions(prev => prev.map(opt => opt.id === moveModalOption.id ? {
      ...opt,
      category: moveToCatalog,
      subcategory: moveToSubcategory,
      variantSet: moveToVariantSet
    } : opt));
    setMoveModalOption(null);
  };
  const moveToSubcategories = useMemo(() => {
    const catalog = catalogs.find(c => c.name === moveToCatalog);
    if (!catalog) return [];
    const managed = catalog.subcategories || [];
    const derived = Array.from(new Set(globalOptions.filter(o => o.category === moveToCatalog).map(o => o.subcategory)));
    return Array.from(new Set([...managed, ...derived])).filter(Boolean);
  }, [moveToCatalog, catalogs, globalOptions]);
  const moveToVariantSets = useMemo(() => {
    return Array.from(new Set(globalOptions.filter(o => o.category === moveToCatalog && o.subcategory === moveToSubcategory).map(o => o.variantSet))).filter(Boolean);
  }, [moveToCatalog, moveToSubcategory, globalOptions]);
  const renderOptionCard = (option: ResolvedOption, isGrid: boolean = true) => {
    const optionLevel = levelAssignments[option.id];
    const levelInfo = optionLevel ? levelConfig[optionLevel] : null;
    return <div key={option.id} className={`bg-white border rounded-lg p-4 flex gap-4 relative transition-all ${option.effectiveEnabled ? 'border-slate-200 shadow-sm' : 'border-slate-100 opacity-75 bg-slate-50'}`} data-id="element-5966">
        <button onClick={() => navigate(`/${builderId}/options/${option.id}`)} className={`${isGrid ? 'h-20 w-20' : 'h-12 w-12'} bg-slate-100 rounded-md flex-shrink-0 flex items-center justify-center border border-slate-100 overflow-hidden hover:ring-2 hover:ring-primary-300 transition-all`} data-id="element-5967">
          {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt={option.effectiveName} className="h-full w-full object-cover" data-id="element-5968" /> : <ImageOff className={`${isGrid ? 'h-8 w-8' : 'h-5 w-5'} text-slate-300`} data-id="element-5969" />}
        </button>
        <div className="flex-1 min-w-0 flex flex-col" data-id="element-5970">
          <div className="flex items-start justify-between mb-1" data-id="element-5971">
            <div className="flex items-center gap-2 min-w-0 flex-wrap" data-id="element-5972">
              <button onClick={() => navigate(`/${builderId}/options/${option.id}`)} className="font-medium text-slate-900 truncate hover:text-primary-600 transition-colors" data-id="element-5973">
                {option.effectiveName}
              </button>
              {isDefaultOption(option) && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 flex-shrink-0" data-id="element-5974">
                  <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" data-id="element-5975" />
                  Default
                </span>}
              {option.effectiveIsStandard && <Badge variant="success" className="text-[10px] px-1.5 py-0.5 flex-shrink-0" data-id="element-5976">
                  <Star className="h-3 w-3 mr-0.5" data-id="element-5977" /> Included
                </Badge>}
              {levelInfo && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border flex-shrink-0 ${levelInfo.bgColor} ${levelInfo.color} ${levelInfo.borderColor}`} data-id="element-5978">
                  {levelInfo.label}
                </span>}
            </div>
            <div className="relative flex-shrink-0" data-id="element-5979">
              <button onClick={() => setOpenDropdown(openDropdown === option.id ? null : option.id)} className="p-1 hover:bg-slate-100 rounded" data-id="element-5980">
                <MoreVertical className="h-4 w-4 text-slate-400" data-id="element-5981" />
              </button>
              {openDropdown === option.id && <>
                  <div className="fixed inset-0 z-[9]" onClick={() => {
                setOpenDropdown(null);
                setLevelPickerOpen(null);
              }} data-id="element-5982" />
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10 min-w-[220px]" data-id="element-5983">
                    <button onClick={() => navigate(`/${builderId}/options/${option.id}`)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-5984">
                      <Eye className="h-4 w-4" data-id="element-5985" /> View Details
                    </button>
                    <button onClick={() => handleOpenEdit(option)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-5986">
                      <Edit className="h-4 w-4" data-id="element-5987" />{' '}
                      {scope.level === 'global' ? 'Edit Option' : `Override for ${scope.level}`}
                    </button>
                    <button onClick={() => handleOpenMoveModal(option)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-5988">
                      <ArrowRightLeft className="h-4 w-4" data-id="element-5989" /> Move to...
                    </button>
                    <button onClick={() => handleSetDefault(option)} className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDefaultOption(option) ? 'text-amber-700 bg-amber-50' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-5990">
                      <Star className={`h-4 w-4 ${isDefaultOption(option) ? 'fill-amber-500 text-amber-500' : ''}`} data-id="element-5991" />
                      {isDefaultOption(option) ? 'Remove Default' : 'Set as Default'}
                    </button>
                    <div className="border-t border-slate-100 my-1" data-id="element-5992" />
                    <div data-id="element-5993">
                      <button onClick={() => setLevelPickerOpen(levelPickerOpen === option.id ? null : option.id)} className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-5994">
                        <div className="flex items-center gap-2" data-id="element-5995">
                          <Layers className="h-4 w-4" data-id="element-5996" />
                          <span data-id="element-5997">Set Level</span>
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-5998">
                          {levelInfo ? <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${levelInfo.bgColor} ${levelInfo.color} ${levelInfo.borderColor}`} data-id="element-5999">
                              {levelInfo.label}
                            </span> : <span className="text-xs text-slate-400" data-id="element-6000">None</span>}
                          <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${levelPickerOpen === option.id ? 'rotate-180' : ''}`} data-id="element-6001" />
                        </div>
                      </button>
                      {levelPickerOpen === option.id && <div className="border-t border-slate-100 mx-2 my-1 pt-1 pb-1 space-y-0.5" data-id="element-6002">
                          <button onClick={() => handleSetLevel(option.id, null)} className={`w-full flex items-center justify-between px-3 py-1.5 text-sm rounded hover:bg-slate-50 ${!optionLevel ? 'text-slate-900 font-medium bg-slate-50' : 'text-slate-600'}`} data-id="element-6003">
                            <span data-id="element-6004">No Level</span>
                            {!optionLevel && <Check className="h-3.5 w-3.5 text-primary-600" data-id="element-6005" />}
                          </button>
                          {Object.entries(levelConfig).map(([key, config]) => <button key={key} onClick={() => handleSetLevel(option.id, key as OptionLevel)} className={`w-full flex items-center justify-between px-3 py-1.5 text-sm rounded hover:bg-slate-50 ${optionLevel === key ? 'font-medium bg-slate-50' : ''}`} data-id="element-6006">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${config.bgColor} ${config.color} ${config.borderColor}`} data-id="element-6007">
                                {config.label}
                              </span>
                              {optionLevel === key && <Check className="h-3.5 w-3.5 text-primary-600" data-id="element-6008" />}
                            </button>)}
                        </div>}
                    </div>
                    {scope.level !== 'global' && option.hasOverride && option.overrideSource === scope.level && <>
                          <div className="border-t border-slate-100 my-1" data-id="element-6009" />
                          <button onClick={() => handleResetOverride(option)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-6010">
                            <Undo2 className="h-4 w-4" data-id="element-6011" /> Reset to Parent
                          </button>
                        </>}
                  </div>
                </>}
            </div>
          </div>
          {isGrid && <p className="text-xs text-slate-500 line-clamp-2 mb-2" data-id="element-6012">
              {option.effectiveDescription}
            </p>}
          <div className={`flex items-center justify-between ${isGrid ? 'mt-auto' : ''}`} data-id="element-6013">
            <div className="flex items-center gap-2" data-id="element-6014">
              {option.effectiveIsStandard ? <p className="text-sm font-semibold text-emerald-600" data-id="element-6015">
                  Standard
                </p> : <>
                  <p className="text-sm font-semibold text-slate-900" data-id="element-6016">
                    ${option.effectivePrice.toLocaleString()}
                  </p>
                  {option.hasOverride && option.parentValue && option.parentValue.price !== option.effectivePrice && <p className="text-xs text-slate-400 line-through" data-id="element-6017">
                        ${option.parentValue.price.toLocaleString()}
                      </p>}
                </>}
            </div>
            {scope.level !== 'global' && <Badge variant="neutral" className={`text-[10px] px-1.5 py-0.5 ${option.hasOverride && option.overrideSource === scope.level ? 'bg-amber-50 text-amber-700 border-amber-200' : option.overrideSource !== 'global' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-600'}`} data-id="element-6018">
                {option.hasOverride && option.overrideSource === scope.level ? <span className="flex items-center gap-1" data-id="element-6019">
                    <Edit className="h-3 w-3" data-id="element-6020" /> Overridden
                  </span> : option.overrideSource !== 'global' ? <span className="flex items-center gap-1" data-id="element-6021">
                    <RefreshCw className="h-3 w-3" data-id="element-6022" /> Inherited
                  </span> : <span className="flex items-center gap-1" data-id="element-6023">
                    <Globe className="h-3 w-3" data-id="element-6024" /> Global
                  </span>}
              </Badge>}
          </div>
        </div>
      </div>;
  };
  // Catalog CRUD handlers
  const handleCreateCatalog = () => {
    if (!newCatalogName.trim()) return;
    const id = `catalog-${newCatalogName.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;
    const colors = ['blue', 'amber', 'emerald', 'purple', 'rose', 'cyan'];
    const color = colors[catalogs.length % colors.length];
    setCatalogs(prev => [...prev, {
      id,
      name: newCatalogName.trim(),
      description: newCatalogDesc.trim(),
      color
    }]);
    setNewCatalogName('');
    setNewCatalogDesc('');
    setCreatingCatalog(false);
  };
  const handleRenameCatalog = (catalogId: string) => {
    if (!renameCatalogName.trim()) return;
    const catalog = catalogs.find(c => c.id === catalogId);
    if (!catalog) return;
    const oldName = catalog.name;
    const newName = renameCatalogName.trim();
    setCatalogs(prev => prev.map(c => c.id === catalogId ? {
      ...c,
      name: newName
    } : c));
    // Update all options that reference the old category name
    setGlobalOptions(prev => prev.map(opt => opt.category === oldName ? {
      ...opt,
      category: newName
    } : opt));
    // Update breadcrumbs if viewing the renamed catalog
    if (breadcrumbs[0]?.value === oldName) {
      setBreadcrumbs(prev => prev.map((b, i) => i === 0 ? {
        ...b,
        label: newName,
        value: newName
      } : b));
    }
    setRenamingCatalogId(null);
    setRenameCatalogName('');
  };
  const handleDeleteCatalog = (catalogId: string) => {
    const catalog = catalogs.find(c => c.id === catalogId);
    if (!catalog) return;
    // Remove the catalog
    setCatalogs(prev => prev.filter(c => c.id !== catalogId));
    // Remove options in this catalog
    setGlobalOptions(prev => prev.filter(opt => opt.category !== catalog.name));
    // Reset breadcrumbs if viewing the deleted catalog
    if (breadcrumbs[0]?.value === catalog.name) {
      setBreadcrumbs([]);
    }
    setDeletingCatalogId(null);
    setCatalogMenuOpen(null);
  };
  const handleStartRename = (catalogId: string) => {
    const catalog = catalogs.find(c => c.id === catalogId);
    if (!catalog) return;
    setRenamingCatalogId(catalogId);
    setRenameCatalogName(catalog.name);
    setCatalogMenuOpen(null);
  };
  return <div className="flex flex-col h-[calc(100vh-8rem)]" data-id="element-6025">
      {/* Move Option Modal */}
      {moveModalOption && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-6026">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" data-id="element-6027">
            <div className="flex items-center justify-between mb-4" data-id="element-6028">
              <div data-id="element-6029">
                <h3 className="text-lg font-semibold text-slate-900" data-id="element-6030">
                  Move Option
                </h3>
                <p className="text-sm text-slate-500" data-id="element-6031">
                  Move "{moveModalOption.effectiveName}" to a different location
                </p>
              </div>
              <button onClick={() => setMoveModalOption(null)} className="text-slate-400 hover:text-slate-600" data-id="element-6032">
                <X className="h-5 w-5" data-id="element-6033" />
              </button>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 mb-4 flex items-center gap-3" data-id="element-6034">
              <div className="h-10 w-10 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-6035">
                {moveModalOption.hasImage && moveModalOption.imageUrl ? <img src={moveModalOption.imageUrl} alt="" className="h-full w-full object-cover" data-id="element-6036" /> : <ImageOff className="h-4 w-4 text-slate-300" data-id="element-6037" />}
              </div>
              <div className="min-w-0" data-id="element-6038">
                <p className="text-sm font-medium text-slate-900 truncate" data-id="element-6039">
                  {moveModalOption.effectiveName}
                </p>
                <p className="text-xs text-slate-500" data-id="element-6040">
                  {moveModalOption.category} › {moveModalOption.subcategory} ›{' '}
                  {moveModalOption.variantSet}
                </p>
              </div>
            </div>

            <div className="space-y-3" data-id="element-6041">
              <div data-id="element-6042">
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-6043">
                  Catalog
                </label>
                <div className="relative" data-id="element-6044">
                  <select value={moveToCatalog} onChange={e => {
                setMoveToCatalog(e.target.value);
                setMoveToSubcategory('');
                setMoveToVariantSet('');
              }} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm appearance-none bg-white pr-8 focus:ring-primary-500 focus:border-primary-500" data-id="element-6045">
                    {catalogs.map(c => <option key={c.id} value={c.name} data-id="element-6046">
                        {c.name}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-6047" />
                </div>
              </div>
              <div data-id="element-6048">
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-6049">
                  Subcategory
                </label>
                <div className="relative" data-id="element-6050">
                  {moveToSubcategories.length > 0 ? <>
                      <select value={moveToSubcategory} onChange={e => {
                  setMoveToSubcategory(e.target.value);
                  setMoveToVariantSet('');
                }} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm appearance-none bg-white pr-8 focus:ring-primary-500 focus:border-primary-500" data-id="element-6051">
                        <option value="" data-id="element-6052">Select subcategory</option>
                        {moveToSubcategories.map(s => <option key={s} value={s} data-id="element-6053">
                            {s}
                          </option>)}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-6054" />
                    </> : <input type="text" value={moveToSubcategory} onChange={e => setMoveToSubcategory(e.target.value)} placeholder="Type new subcategory name" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500" data-id="element-6055" />}
                </div>
              </div>
              <div data-id="element-6056">
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-6057">
                  Variant Set
                </label>
                {moveToVariantSets.length > 0 ? <div className="relative" data-id="element-6058">
                    <select value={moveToVariantSet} onChange={e => setMoveToVariantSet(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm appearance-none bg-white pr-8 focus:ring-primary-500 focus:border-primary-500" data-id="element-6059">
                      <option value="" data-id="element-6060">Select variant set</option>
                      {moveToVariantSets.map(v => <option key={v} value={v} data-id="element-6061">
                          {v}
                        </option>)}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-6062" />
                  </div> : <input type="text" value={moveToVariantSet} onChange={e => setMoveToVariantSet(e.target.value)} placeholder="Type new variant set name" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500" data-id="element-6063" />}
              </div>
            </div>

            {moveToCatalog !== moveModalOption.category || moveToSubcategory !== moveModalOption.subcategory || moveToVariantSet !== moveModalOption.variantSet ? <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-6064">
                <p className="text-xs text-blue-800" data-id="element-6065">
                  <span className="font-medium" data-id="element-6066">Moving to:</span>{' '}
                  {moveToCatalog} › {moveToSubcategory || '...'} ›{' '}
                  {moveToVariantSet || '...'}
                </p>
              </div> : null}

            <div className="flex items-center justify-end gap-3 mt-5" data-id="element-6067">
              <Button variant="ghost" size="sm" onClick={() => setMoveModalOption(null)} data-id="element-6068">
                Cancel
              </Button>
              <Button size="sm" onClick={handleMoveOption} disabled={!moveToCatalog.trim() || !moveToSubcategory.trim() || !moveToVariantSet.trim() || moveToCatalog === moveModalOption.category && moveToSubcategory === moveModalOption.subcategory && moveToVariantSet === moveModalOption.variantSet} leftIcon={<ArrowRightLeft className="h-3.5 w-3.5" data-id="element-6070" />} data-id="element-6069">
                Move Option
              </Button>
            </div>
          </div>
        </div>}

      {/* Delete Catalog Confirmation Modal */}
      {deletingCatalogId && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-6071">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6" data-id="element-6072">
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-6073">
              Delete Catalog
            </h3>
            <p className="text-sm text-slate-600 mb-1" data-id="element-6074">
              Are you sure you want to delete{' '}
              <span className="font-medium" data-id="element-6075">
                "{catalogs.find(c => c.id === deletingCatalogId)?.name}"
              </span>
              ?
            </p>
            <p className="text-xs text-red-600 mb-4" data-id="element-6076">
              This will also remove all{' '}
              {globalOptions.filter(o => o.category === catalogs.find(c => c.id === deletingCatalogId)?.name).length}{' '}
              options in this catalog.
            </p>
            <div className="flex items-center justify-end gap-3" data-id="element-6077">
              <Button variant="ghost" size="sm" onClick={() => setDeletingCatalogId(null)} data-id="element-6078">
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={() => handleDeleteCatalog(deletingCatalogId)} data-id="element-6079">
                Delete Catalog
              </Button>
            </div>
          </div>
        </div>}

      {selectedOptionForEdit && <EditOptionModal isOpen={editModalOpen} onClose={() => {
      setEditModalOpen(false);
      setSelectedOptionForEdit(null);
    }} option={selectedOptionForEdit} scope={scope} onSave={handleSaveOverride} onSaveGlobal={handleSaveGlobal} data-id="element-6080" />}
      <CreateOptionModal isOpen={createModalOpen} onClose={() => setCreateModalOpen(false)} existingOptions={globalOptions} onCreateNew={handleCreateNewOption} onLinkExisting={handleLinkExistingOption} breadcrumbs={breadcrumbs} catalogs={catalogs} data-id="element-6081" />

      <div className="flex gap-6 flex-1 overflow-hidden" data-id="element-6082">
        <div className="w-64 flex-shrink-0 flex flex-col gap-4" data-id="element-6083">
          <ScopeSelector currentScope={scope} onChange={setScope} data-id="element-6084" />
          <Card className="flex-1 flex flex-col overflow-hidden" noPadding data-id="element-6085">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between" data-id="element-6086">
              <h3 className="font-semibold text-slate-900" data-id="element-6087">Catalogs</h3>
              <button onClick={() => {
              setCreatingCatalog(true);
              setNewCatalogName('');
              setNewCatalogDesc('');
            }} className="p-1 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700 transition-colors" title="Create new catalog" data-id="element-6088">
                <Plus className="h-4 w-4" data-id="element-6089" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2" data-id="element-6090">
              <button onClick={() => setBreadcrumbs([])} className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${breadcrumbs.length === 0 ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-6091">
                <Home className="h-4 w-4" data-id="element-6092" />
                All Catalogs
              </button>
              <div className="mt-2 space-y-1" data-id="element-6093">
                {catalogs.map(catalog => {
                const cat = catalog.name;
                const isActive = breadcrumbs[0]?.value === cat;
                const isExpanded = expandedCategories.has(cat);
                const items = resolvedOptions.filter(o => o.category === cat);
                const allSubcategories = getSubcategoriesForCatalog(catalog.id);
                const isRenaming = renamingCatalogId === catalog.id;
                return <div key={catalog.id} data-id="element-6094">
                      <div className="flex items-center gap-1 group" data-id="element-6095">
                        <button onClick={() => toggleCategory(cat)} className="p-1 hover:bg-slate-100 rounded transition-colors" data-id="element-6096">
                          {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" data-id="element-6097" /> : <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-6098" />}
                        </button>
                        {isRenaming ? <div className="flex-1 flex items-center gap-1" data-id="element-6099">
                            <input type="text" value={renameCatalogName} onChange={e => setRenameCatalogName(e.target.value)} onKeyDown={e => {
                        if (e.key === 'Enter') handleRenameCatalog(catalog.id);
                        if (e.key === 'Escape') {
                          setRenamingCatalogId(null);
                          setRenameCatalogName('');
                        }
                      }} className="flex-1 px-2 py-1 text-sm border border-primary-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none" autoFocus data-id="element-6100" />
                            <button onClick={() => handleRenameCatalog(catalog.id)} className="p-1 text-primary-600 hover:bg-primary-50 rounded" data-id="element-6101">
                              <Check className="h-3.5 w-3.5" data-id="element-6102" />
                            </button>
                            <button onClick={() => {
                        setRenamingCatalogId(null);
                        setRenameCatalogName('');
                      }} className="p-1 text-slate-400 hover:bg-slate-100 rounded" data-id="element-6103">
                              <X className="h-3.5 w-3.5" data-id="element-6104" />
                            </button>
                          </div> : <>
                            <button onClick={() => handleSidebarNavigate(cat)} className={`flex-1 flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${isActive && breadcrumbs.length === 1 ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-6105">
                              <span className="truncate" data-id="element-6106">{cat}</span>
                              <Badge variant={isActive && breadcrumbs.length === 1 ? 'primary' : 'neutral'} className="text-xs" data-id="element-6107">
                                {items.length}
                              </Badge>
                            </button>
                            <div className="relative" data-id="element-6108">
                              <button onClick={e => {
                          e.stopPropagation();
                          setCatalogMenuOpen(catalogMenuOpen === catalog.id ? null : catalog.id);
                        }} className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all" data-id="element-6109">
                                <MoreVertical className="h-3.5 w-3.5" data-id="element-6110" />
                              </button>
                              {catalogMenuOpen === catalog.id && <>
                                  <div className="fixed inset-0 z-[9]" onClick={() => setCatalogMenuOpen(null)} data-id="element-6111" />
                                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10 min-w-[160px]" data-id="element-6112">
                                    <button onClick={() => handleStartRename(catalog.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-6113">
                                      <Pencil className="h-3.5 w-3.5" data-id="element-6114" /> Rename
                                    </button>
                                    <div className="border-t border-slate-100 my-1" data-id="element-6115" />
                                    <button onClick={() => {
                              setDeletingCatalogId(catalog.id);
                              setCatalogMenuOpen(null);
                            }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-6116">
                                      <Trash2 className="h-3.5 w-3.5" data-id="element-6117" /> Delete
                                    </button>
                                  </div>
                                </>}
                            </div>
                          </>}
                      </div>
                      {isExpanded && !isRenaming && <div className="ml-6 mt-1 space-y-0.5" data-id="element-6118">
                          {allSubcategories.map(subcat => {
                      const isSubActive = breadcrumbs[0]?.value === cat && breadcrumbs[1]?.value === subcat;
                      const subcatItems = resolvedOptions.filter(o => o.category === cat && o.subcategory === subcat);
                      const subcatKey = `${catalog.id}:${subcat}`;
                      const isRenamingThis = renamingSubcat?.catalogId === catalog.id && renamingSubcat?.oldName === subcat;
                      if (isRenamingThis) {
                        return <div key={subcat} className="flex items-center gap-1" data-id="element-6119">
                                  <input type="text" value={renameSubcatName} onChange={e => setRenameSubcatName(e.target.value)} onKeyDown={e => {
                            if (e.key === 'Enter') handleRenameSubcategory(catalog.id, subcat);
                            if (e.key === 'Escape') {
                              setRenamingSubcat(null);
                              setRenameSubcatName('');
                            }
                          }} className="flex-1 px-2 py-1 text-sm border border-primary-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none" autoFocus data-id="element-6120" />
                                  <button onClick={() => handleRenameSubcategory(catalog.id, subcat)} className="p-0.5 text-primary-600 hover:bg-primary-50 rounded" data-id="element-6121">
                                    <Check className="h-3 w-3" data-id="element-6122" />
                                  </button>
                                  <button onClick={() => {
                            setRenamingSubcat(null);
                            setRenameSubcatName('');
                          }} className="p-0.5 text-slate-400 hover:bg-slate-100 rounded" data-id="element-6123">
                                    <X className="h-3 w-3" data-id="element-6124" />
                                  </button>
                                </div>;
                      }
                      const subcatExpandKey = `${cat}::${subcat}`;
                      const isSubExpanded = expandedSubcategories.has(subcatExpandKey);
                      const variantSetsForSubcat = getVariantSets(cat, subcat);
                      return <div key={subcat} data-id="element-6125">
                                <div className="flex items-center group/subcat" data-id="element-6126">
                                  {variantSetsForSubcat.length > 0 && <button onClick={() => toggleSubcategory(subcatExpandKey)} className="p-0.5 hover:bg-slate-100 rounded transition-colors" data-id="element-6127">
                                      {isSubExpanded ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" data-id="element-6128" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" data-id="element-6129" />}
                                    </button>}
                                  <button onClick={() => {
                            handleSidebarNavigate(cat, subcat);
                            if (variantSetsForSubcat.length > 0 && !isSubExpanded) {
                              toggleSubcategory(subcatExpandKey);
                            }
                          }} className={`flex-1 flex items-center justify-between px-2 py-1.5 rounded-md text-sm transition-colors ${isSubActive && breadcrumbs.length === 2 ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-6130">
                                    <span className="truncate" data-id="element-6131">{subcat}</span>
                                    <Badge variant={isSubActive && breadcrumbs.length === 2 ? 'primary' : 'neutral'} className="text-xs ml-2" data-id="element-6132">
                                      {subcatItems.length}
                                    </Badge>
                                  </button>
                                  <div className="relative" data-id="element-6133">
                                    <button onClick={e => {
                              e.stopPropagation();
                              setSubcatMenuOpen(subcatMenuOpen === subcatKey ? null : subcatKey);
                            }} className="p-0.5 rounded opacity-0 group-hover/subcat:opacity-100 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all" data-id="element-6134">
                                      <MoreVertical className="h-3 w-3" data-id="element-6135" />
                                    </button>
                                    {subcatMenuOpen === subcatKey && <>
                                        <div className="fixed inset-0 z-[9]" onClick={() => setSubcatMenuOpen(null)} data-id="element-6136" />
                                        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10 min-w-[150px]" data-id="element-6137">
                                          <button onClick={() => {
                                  setRenamingSubcat({
                                    catalogId: catalog.id,
                                    oldName: subcat
                                  });
                                  setRenameSubcatName(subcat);
                                  setSubcatMenuOpen(null);
                                }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50" data-id="element-6138">
                                            <Pencil className="h-3 w-3" data-id="element-6139" />{' '}
                                            Rename
                                          </button>
                                          <div className="border-t border-slate-100 my-1" data-id="element-6140" />
                                          <button onClick={() => handleDeleteSubcategory(catalog.id, subcat)} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50" data-id="element-6141">
                                            <Trash2 className="h-3 w-3" data-id="element-6142" />{' '}
                                            Delete
                                          </button>
                                        </div>
                                      </>}
                                  </div>
                                </div>

                                {/* Variant Sets (3rd level) */}
                                {isSubExpanded && variantSetsForSubcat.length > 0 && <div className="ml-5 mt-0.5 space-y-0.5 mb-1" data-id="element-6143">
                                      {variantSetsForSubcat.map(vs => {
                            const isVsActive = breadcrumbs[0]?.value === cat && breadcrumbs[1]?.value === subcat && breadcrumbs[2]?.value === vs;
                            const vsItems = resolvedOptions.filter(o => o.category === cat && o.subcategory === subcat && o.variantSet === vs);
                            return <button key={vs} onClick={() => handleSidebarNavigate(cat, subcat, vs)} className={`w-full flex items-center justify-between px-3 py-1 rounded-md text-xs transition-colors ${isVsActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`} data-id="element-6144">
                                            <span className="truncate" data-id="element-6145">
                                              {vs}
                                            </span>
                                            <Badge variant={isVsActive ? 'primary' : 'neutral'} className="text-[10px] ml-2" data-id="element-6146">
                                              {vsItems.length}
                                            </Badge>
                                          </button>;
                          })}
                                    </div>}
                              </div>;
                    })}

                          {/* Add subcategory inline form */}
                          {creatingSubcatForCatalog === catalog.id ? <div className="flex items-center gap-1 px-1" data-id="element-6147">
                              <input type="text" value={newSubcatName} onChange={e => setNewSubcatName(e.target.value)} onKeyDown={e => {
                        if (e.key === 'Enter') handleCreateSubcategory(catalog.id);
                        if (e.key === 'Escape') {
                          setCreatingSubcatForCatalog(null);
                          setNewSubcatName('');
                        }
                      }} placeholder="Subcategory name" className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none" autoFocus data-id="element-6148" />
                              <button onClick={() => handleCreateSubcategory(catalog.id)} disabled={!newSubcatName.trim()} className="p-0.5 text-primary-600 hover:bg-primary-50 rounded disabled:opacity-40" data-id="element-6149">
                                <Check className="h-3 w-3" data-id="element-6150" />
                              </button>
                              <button onClick={() => {
                        setCreatingSubcatForCatalog(null);
                        setNewSubcatName('');
                      }} className="p-0.5 text-slate-400 hover:bg-slate-100 rounded" data-id="element-6151">
                                <X className="h-3 w-3" data-id="element-6152" />
                              </button>
                            </div> : <button onClick={() => {
                      setCreatingSubcatForCatalog(catalog.id);
                      setNewSubcatName('');
                    }} className="w-full flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-6153">
                              <Plus className="h-3 w-3" data-id="element-6154" /> Add subcategory
                            </button>}
                        </div>}
                    </div>;
              })}

                {/* Inline create catalog form */}
                {creatingCatalog && <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-id="element-6155">
                    <div className="flex items-center gap-2 mb-1" data-id="element-6156">
                      <FolderPlus className="h-4 w-4 text-primary-500" data-id="element-6157" />
                      <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider" data-id="element-6158">
                        New Catalog
                      </span>
                    </div>
                    <input type="text" placeholder="Catalog name" value={newCatalogName} onChange={e => setNewCatalogName(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && newCatalogName.trim()) handleCreateCatalog();
                  if (e.key === 'Escape') setCreatingCatalog(false);
                }} className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none" autoFocus data-id="element-6159" />
                    <input type="text" placeholder="Description (optional)" value={newCatalogDesc} onChange={e => setNewCatalogDesc(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && newCatalogName.trim()) handleCreateCatalog();
                  if (e.key === 'Escape') setCreatingCatalog(false);
                }} className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-6160" />
                    <div className="flex items-center gap-2 pt-1" data-id="element-6161">
                      <button onClick={handleCreateCatalog} disabled={!newCatalogName.trim()} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-6162">
                        <Check className="h-3 w-3" data-id="element-6163" /> Create
                      </button>
                      <button onClick={() => setCreatingCatalog(false)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors" data-id="element-6164">
                        Cancel
                      </button>
                    </div>
                  </div>}

                {catalogs.length === 0 && !creatingCatalog && <div className="px-3 py-6 text-center" data-id="element-6165">
                    <Layers className="h-8 w-8 text-slate-300 mx-auto mb-2" data-id="element-6166" />
                    <p className="text-sm text-slate-500 mb-1" data-id="element-6167">
                      No catalogs yet
                    </p>
                    <p className="text-xs text-slate-400 mb-3" data-id="element-6168">
                      Create a catalog to organize your options
                    </p>
                    <button onClick={() => setCreatingCatalog(true)} className="text-xs font-medium text-primary-600 hover:text-primary-700" data-id="element-6169">
                      + Create your first catalog
                    </button>
                  </div>}
              </div>
            </div>
          </Card>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden" data-id="element-6170">
          <div className="flex items-center justify-between mb-6" data-id="element-6171">
            <div data-id="element-6172">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-6173">
                Options Catalog
              </h1>
              <p className="text-sm text-slate-500" data-id="element-6174">
                Manage and override options across levels
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-6175">
              <Button variant="ghost" leftIcon={<Tag className="h-4 w-4" data-id="element-6177" />} onClick={() => navigate(`/${builderId}/options/tags`)} data-id="element-6176">
                Manage Tags
              </Button>
              <Button variant="outline" leftIcon={<Plus className="h-4 w-4" data-id="element-6179" />} onClick={() => setCreateModalOpen(true)} data-id="element-6178">
                Add Option
              </Button>
              <Button leftIcon={<Save className="h-4 w-4" data-id="element-6181" />} data-id="element-6180">
                Save Changes
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-4 mb-6" data-id="element-6182">
            <Card noPadding data-id="element-6183">
              <div className="p-4" data-id="element-6184">
                <p className="text-sm text-slate-500 mb-1" data-id="element-6185">Total Options</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-6186">
                  {stats.total}
                </p>
              </div>
            </Card>
            <Card noPadding data-id="element-6187">
              <div className="p-4" data-id="element-6188">
                <p className="text-sm text-slate-500 mb-1" data-id="element-6189">Enabled</p>
                <p className="text-2xl font-bold text-green-600" data-id="element-6190">
                  {stats.enabled}
                </p>
              </div>
            </Card>
            <Card noPadding data-id="element-6191">
              <div className="p-4" data-id="element-6192">
                <p className="text-sm text-slate-500 mb-1" data-id="element-6193">Standard</p>
                <div className="flex items-center gap-2" data-id="element-6194">
                  <p className="text-2xl font-bold text-emerald-600" data-id="element-6195">
                    {stats.standard}
                  </p>
                  <Badge variant="success" className="text-xs" data-id="element-6196">
                    <Star className="h-3 w-3" data-id="element-6197" />
                  </Badge>
                </div>
              </div>
            </Card>
            <Card noPadding className={scope.level === 'global' ? 'opacity-50' : ''} data-id="element-6198">
              <div className="p-4" data-id="element-6199">
                <p className="text-sm text-slate-500 mb-1" data-id="element-6200">Inherited</p>
                <p className="text-2xl font-bold text-blue-600" data-id="element-6201">
                  {stats.inherited}
                </p>
              </div>
            </Card>
            <Card noPadding className={scope.level === 'global' ? 'opacity-50' : ''} data-id="element-6202">
              <div className="p-4" data-id="element-6203">
                <p className="text-sm text-slate-500 mb-1" data-id="element-6204">Overridden</p>
                <p className="text-2xl font-bold text-amber-600" data-id="element-6205">
                  {stats.overridden}
                </p>
              </div>
            </Card>
          </div>

          <Card className="mb-6" noPadding data-id="element-6206">
            <div className="p-4 flex flex-col gap-4" data-id="element-6207">
              <div className="flex items-center gap-3" data-id="element-6208">
                <div className="relative" data-id="element-6209">
                  <button onClick={() => setLevelDropdownOpen(!levelDropdownOpen)} className={`flex items-center gap-2 px-3 py-2 border rounded-md text-sm font-medium transition-colors whitespace-nowrap ${levelFilter !== 'all' ? 'bg-primary-50 border-primary-200 text-primary-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} data-id="element-6210">
                    <Layers className="h-4 w-4" data-id="element-6211" />
                    {levelFilter === 'all' ? 'All Levels' : levelFilter === null ? 'Unassigned' : levelConfig[levelFilter]?.label}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${levelDropdownOpen ? 'rotate-180' : ''}`} data-id="element-6212" />
                  </button>
                  {levelDropdownOpen && <>
                      <div className="fixed inset-0 z-40" onClick={() => setLevelDropdownOpen(false)} data-id="element-6213" />
                      <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50 min-w-[200px]" data-id="element-6214">
                        <button onClick={() => {
                      setLevelFilter('all');
                      setLevelDropdownOpen(false);
                    }} className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 ${levelFilter === 'all' ? 'font-medium text-slate-900' : 'text-slate-600'}`} data-id="element-6215">
                          <span data-id="element-6216">All Levels</span>
                          <div className="flex items-center gap-2" data-id="element-6217">
                            <span className="text-xs text-slate-400" data-id="element-6218">
                              {resolvedOptions.length}
                            </span>
                            {levelFilter === 'all' && <Check className="h-3.5 w-3.5 text-primary-600" data-id="element-6219" />}
                          </div>
                        </button>
                        <div className="border-t border-slate-100 my-1" data-id="element-6220" />
                        {Object.entries(levelConfig).map(([key, config]) => <button key={key} onClick={() => {
                      setLevelFilter(key as OptionLevel);
                      setLevelDropdownOpen(false);
                    }} className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 ${levelFilter === key ? 'font-medium' : ''}`} data-id="element-6221">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${config.bgColor} ${config.color} ${config.borderColor}`} data-id="element-6222">
                              {config.label}
                            </span>
                            <div className="flex items-center gap-2" data-id="element-6223">
                              <span className="text-xs text-slate-400" data-id="element-6224">
                                {levelStats[key] || 0}
                              </span>
                              {levelFilter === key && <Check className="h-3.5 w-3.5 text-primary-600" data-id="element-6225" />}
                            </div>
                          </button>)}
                        <div className="border-t border-slate-100 my-1" data-id="element-6226" />
                        <button onClick={() => {
                      setLevelFilter(null);
                      setLevelDropdownOpen(false);
                    }} className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 ${levelFilter === null ? 'font-medium text-slate-900' : 'text-slate-600'}`} data-id="element-6227">
                          <span data-id="element-6228">Unassigned</span>
                          <div className="flex items-center gap-2" data-id="element-6229">
                            <span className="text-xs text-slate-400" data-id="element-6230">
                              {levelStats['unassigned'] || 0}
                            </span>
                            {levelFilter === null && <Check className="h-3.5 w-3.5 text-primary-600" data-id="element-6231" />}
                          </div>
                        </button>
                      </div>
                    </>}
                </div>
                <div className="flex-1 relative" data-id="element-6232">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" data-id="element-6233" />
                  <input type="text" placeholder="Search options..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6234" />
                </div>
                {isShowingOptions && <div className="flex border border-slate-300 rounded-md overflow-hidden" data-id="element-6235">
                    <button onClick={() => setViewMode('grid')} className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-6236">
                      <Grid3x3 className="h-4 w-4" data-id="element-6237" />
                    </button>
                    <button onClick={() => setViewMode('list')} className={`px-3 py-2 border-l border-slate-300 ${viewMode === 'list' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-6238">
                      <List className="h-4 w-4" data-id="element-6239" />
                    </button>
                  </div>}
              </div>
              {breadcrumbs.length > 0 && !searchQuery && <div className="flex items-center gap-2 text-sm text-slate-600 pt-2 border-t border-slate-100" data-id="element-6240">
                  <button onClick={() => setBreadcrumbs([])} className="hover:text-slate-900" data-id="element-6241">
                    Home
                  </button>
                  {breadcrumbs.map((crumb, index) => <Fragment key={index} data-id="element-6242">
                      <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-6243" />
                      <button onClick={() => handleBreadcrumbClick(index)} className={`hover:text-slate-900 ${index === breadcrumbs.length - 1 ? 'font-medium text-slate-900' : ''}`} data-id="element-6244">
                        {crumb.label}
                      </button>
                    </Fragment>)}
                </div>}
            </div>
          </Card>

          <div className="flex-1 overflow-y-auto pb-8" data-id="element-6245">
            {!isShowingOptions ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-6246">
                {currentItems.map((item: any) => <button key={item.id} onClick={() => handleNavigate(item)} className="bg-white border border-slate-200 rounded-lg p-6 hover:border-primary-300 hover:shadow-md transition-all text-left group" data-id="element-6247">
                    <div className="flex items-start justify-between mb-3" data-id="element-6248">
                      <h4 className="text-lg font-semibold text-slate-900 group-hover:text-primary-600 transition-colors" data-id="element-6249">
                        {item.name}
                      </h4>
                      <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors" data-id="element-6250" />
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600" data-id="element-6251">
                      <span data-id="element-6252">{item.count} options</span>
                      <span className="text-green-600" data-id="element-6253">
                        {item.enabledCount} enabled
                      </span>
                    </div>
                  </button>)}
              </div> : <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4' : 'space-y-2'} data-id="element-6254">
                {currentItems.map(item => renderOptionCard(item as ResolvedOption, viewMode === 'grid'))}
              </div>}
          </div>
        </div>
      </div>
    </div>;
}